import { useEffect, useMemo, useState } from "react";
import ContainerCustom from "../../../Components/ContainerCustom";
import Table from "../../../Components/Table/Table";
import { formatTradePrice } from "../../../helper/formatTradePrice";
import { toJalaliDateTime } from "../../../helper/toJalaliDateTime";
import { API_BASE_URL } from "../../../config/api";

const TRADES_ENDPOINT = `${API_BASE_URL}/api/v0/trading/admin/trades`;
const REFRESH_ENDPOINT =
  `${API_BASE_URL}/api/v0/auth/admin/jwt/refresh/`;

const STATUS_OPEN = "open";
const STATUS_CLOSED = "closed";

const columns = [
  { key: "uuid", label: "کد معامله" },
  { key: "user", label: "کاربر" },
  { key: "openTime", label: "زمان باز شدن" },
  { key: "openPrice", label: "قیمت باز شدن" },
  { key: "closeTime", label: "زمان بسته شدن" },
  { key: "closePrice", label: "قیمت بسته شدن" },
  { key: "pnl", label: "سود" },
  { key: "volume", label: "حجم" },
  { key: "commission", label: "کمیسیون" },
  { key: "netPNL", label: "سود خالص" },
  { key: "symbol", label: "نماد" },
  { key: "tradeType", label: "نوع معامله" },
];

function normalizeTrades(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.trades)) return data.trades;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

function normalizePositionStatus(status) {
  const value = String(status || "").toLowerCase();
  if (value.includes("open")) return STATUS_OPEN;
  if (value.includes("close")) return STATUS_CLOSED;
  return value;
}

function formatJalaliTime(epochValue, isoValue) {
  const epoch = Number(epochValue);
  if (Number.isFinite(epoch) && epoch > 0) {
    return toJalaliDateTime(epoch);
  }

  if (isoValue) {
    const parsed = Date.parse(isoValue);
    if (!Number.isNaN(parsed)) {
      return toJalaliDateTime(Math.floor(parsed / 1000));
    }
  }

  return "-";
}

function formatMoney(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "-";

  const formatted = Math.abs(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return amount < 0 ? `-$${formatted}` : `$${formatted}`;
}

function formatVolume(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "-";

  return amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function normalizeTradeType(side) {
  const sideValue = String(side || "").toLowerCase();
  if (sideValue === "long" || sideValue === "buy") return "Buy";
  if (sideValue === "short" || sideValue === "sell") return "Sell";
  return "-";
}

export default function TradesHistory() {
  const [activeFilter, setActiveFilter] = useState([true, true]);
  const [tradeData, setTradeData] = useState([]);

  useEffect(() => {
    const fetchTradeData = async () => {
      try {
        let response = await fetch(TRADES_ENDPOINT, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
        });

        if (response.status === 401) {
          const refreshResponse = await fetch(REFRESH_ENDPOINT, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              refresh: localStorage.getItem("refresh"),
            }),
          });

          if (!refreshResponse.ok) {
            localStorage.removeItem("access");
            localStorage.removeItem("authCredentials");
            localStorage.removeItem("refresh");
            localStorage.removeItem("role");
            localStorage.removeItem("email");
            window.location.href = "/signin";
            return;
          }

          const refreshData = await refreshResponse.json();
          localStorage.setItem("access", refreshData.access);

          response = await fetch(TRADES_ENDPOINT, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("access")}`,
            },
          });
        }

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        setTradeData(normalizeTrades(data));
      } catch (error) {
        console.error("Error fetching trade data:", error);
      }
    };

    fetchTradeData();
  }, []);

  const tableData = useMemo(
    () =>
      tradeData
        .filter((trade) => {
          const status = normalizePositionStatus(trade.position_status);
          const statusFilterMap = {
            [STATUS_OPEN]: activeFilter[0],
            [STATUS_CLOSED]: activeFilter[1],
          };
          return statusFilterMap[status];
        })
        .map((trade) => ({
          uuid: trade.position_code ?? "-",
          user: trade.user_full_name ?? trade.user ?? trade.user_email ?? "-",
          openTime: formatJalaliTime(trade.opened_at, trade.opened_at_iso),
          openPrice: formatTradePrice(trade.open_price),
          closeTime: formatJalaliTime(trade.closed_at, trade.closed_at_iso),
          closePrice: formatTradePrice(trade.close_price ?? trade.exit_price),
          pnl: formatMoney(trade.profit_loss),
          volume: formatVolume(trade.volume),
          commission: "$0.00",
          netPNL: formatMoney(trade.profit_loss),
          symbol: trade.symbol ? String(trade.symbol).toUpperCase() : "-",
          tradeType: normalizeTradeType(trade.side),
        })),
    [activeFilter, tradeData],
  );

  return (
    <ContainerCustom
      header="تاریخچه معاملات"
      headSVG="archive"
      colorfulFilterOptions={[
        {
          SVGName: "stall",
          text: "معاملات باز",
          className: "w-28",
          color: "yellow",
        },
        {
          SVGName: "tick",
          text: "معاملات بسته شده",
          className: "w-32",
          color: "green",
        },
      ]}
      activeFilter={activeFilter}
      setActiveFilter={setActiveFilter}
      filter={false}
      sort={false}
      search={false}
    >
      <Table isLTR={true} columns={columns} data={tableData} />
    </ContainerCustom>
  );
}
